import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  PhotoLibrary as ImageIcon,
  Description as DocumentIcon,
  Code as CodeIcon,
  TableChart as DataIcon,
  AttachFile as AttachIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { PageContainer } from '../components/common';
import { PageTitleContainer } from '../components/common';
import useProject from '../hooks/useProject';
import fileUploadService from '../services/fileUploadService';
import useNotification from '../hooks/useNotification';

const UploadedDocuments = () => {
  const { projectId } = useParams();
  const { isLoadingProject } = useProject({ projectId });
  const { showNotification } = useNotification();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Load files for this project
  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fileUploadService.getFiles({ projectId, limit: 100 });
      setFiles(response?.files || []);
    } catch (error) {
      console.error('Error loading files:', error);
      showNotification({ message: 'Failed to load files', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [projectId, showNotification]);

  useEffect(() => {
    if (projectId) {
      loadFiles();
    }
  }, [projectId, loadFiles]);

  // Delete handler
  const handleDeleteRequest = (file) => {
    setFileToDelete(file);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await fileUploadService.deleteFile(fileToDelete.id);
      showNotification({ message: 'File deleted successfully', type: 'success' });
      setFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
    } catch (error) {
      showNotification({ message: 'Failed to delete file', type: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setFileToDelete(null);
    }
  };

  // Download handler
  const handleDownload = async (file) => {
    try {
      await fileUploadService.downloadFile(file.id, file.original_filename || file.filename);
    } catch (error) {
      showNotification({ message: 'Failed to download file', type: 'error' });
    }
  };

  // View file in new tab
  const handleViewFile = (file) => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_API_PORT || '8003'}/api`;
    const url = `${apiUrl}/files/${file.id}/download?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  // File type helpers
  const getFileIcon = (file) => {
    const category = file.category || fileUploadService.getFileTypeCategory(file.original_filename || file.filename || '');
    const iconSx = { fontSize: 22 };
    switch (category) {
      case 'image': return <ImageIcon sx={{ ...iconSx, color: '#1976d2' }} />;
      case 'document': return <DocumentIcon sx={{ ...iconSx, color: '#7b1fa2' }} />;
      case 'code': return <CodeIcon sx={{ ...iconSx, color: '#0288d1' }} />;
      case 'data': case 'office': return <DataIcon sx={{ ...iconSx, color: '#2e7d32' }} />;
      default: return <AttachIcon sx={{ ...iconSx, color: '#757575' }} />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'image': return '#e3f2fd';
      case 'document': return '#f3e5f5';
      case 'code': return '#e1f5fe';
      case 'data': case 'office': return '#e8f5e9';
      default: return '#f5f5f5';
    }
  };

  // Filter files by filename and task title
  const filteredFiles = files.filter(file => {
    const query = searchQuery.toLowerCase();
    const name = (file.original_filename || file.filename || '').toLowerCase();
    const taskTitle = (file.task_title || '').toLowerCase();
    return name.includes(query) || taskTitle.includes(query);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Get display label for category
  const getCategoryLabel = (category) => {
    switch (category) {
      case 'image': return 'Image';
      case 'document': return 'Document';
      case 'code': return 'Code';
      case 'data': return 'Data';
      case 'office': return 'Office';
      default: return 'Other';
    }
  };

  if (isLoadingProject) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageContainer
      fullWidth
      sx={{
        px: 3, py: 3,
        display: 'flex', flexDirection: 'column',
        height: '100vh', overflow: 'hidden',
        '& .MuiContainer-root': { maxWidth: '100% !important', padding: '0 !important' }
      }}
    >
      {/* Header */}
      <PageTitleContainer
        title="Project Uploaded Documents"
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0, mr: 0, mb: 2, mt: 0, py: '16px'
        }}
      />

      {/* Main Content */}
      <Box sx={{
        flex: 1, overflow: 'auto', bgcolor: 'white',
        borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex', flexDirection: 'column',
        '&::-webkit-scrollbar': { width: '8px', backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-track': { borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.05)' },
        '&::-webkit-scrollbar-thumb': { borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.2)', '&:hover': { backgroundColor: 'rgba(25,118,210,0.4)' } }
      }}>
        <Box sx={{ p: 3 }}>
          {/* Toolbar: Search */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
              <TextField
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: '10px', }}>
                      <SearchIcon sx={{ color: '#a2abb3', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: 250,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue, #475969)',
                    height: '40px',
                    transition: 'all 0.2s',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    padding: '0px !important',
                    color: 'var(--color-blue, #475969)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px',
                  }
                }}
              />
          </Box>
          <Box sx={{
            height: 'calc(100vh - 300px)',
            overflow: 'auto'
          }}>

            {/* File Cards */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : filteredFiles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <AttachIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  {files.length === 0 ? 'No files uploaded yet' : 'No files match your search'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.5 }}>
                  {files.length === 0 ? 'No documents have been uploaded to this project' : 'Try adjusting your search'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
                gap: 2.5
              }}>
                {filteredFiles.map((file) => {
                  const fileName = file.original_filename || file.filename || 'Unknown file';
                  const category = file.category || fileUploadService.getFileTypeCategory(fileName);
                  const uploaderName = file.uploaded_by_name || 'Unknown';
                  const taskTitle = file.task_title || fileName;
                  return (
                    <Box
                      key={file.id}
                      sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        p: 2.5,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#cbd5e1',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }
                      }}
                    >
                      {/* Card Header: Task Title + Actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{
                          fontWeight: 600, color: '#1e293b', fontSize: '15px',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          flex: 1, mr: 1
                        }}>
                          {taskTitle}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handleViewFile(file)}
                              sx={{ color: '#64748b', '&:hover': { color: '#1e293b' } }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(file)}
                              sx={{ color: '#64748b', '&:hover': { color: '#1976d2' } }}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteRequest(file)}
                              sx={{ color: '#64748b', '&:hover': { color: '#d32f2f' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Uploaded by */}
                      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', mb: 1.5, fontSize: '13px' }}>
                        Uploaded by {uploaderName} on {formatDate(file.created_at)}
                      </Typography>

                      {/* Type */}
                      <Typography variant="body2" sx={{ color: '#475569', fontSize: '13px' }}>
                        <Box component="span" sx={{ color: '#94a3b8' }}>Type:</Box>
                        {'  '}{getCategoryLabel(category)}
                      </Typography>

                      {/* File link row */}
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 1.5,
                          borderTop: '1px solid #f1f5f9', cursor: 'pointer',
                          '&:hover .file-link-text': { color: '#1565c0' }
                        }}
                        onClick={() => handleViewFile(file)}
                      >
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '6px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: getCategoryColor(category), flexShrink: 0
                        }}>
                          {getFileIcon(file)}
                        </Box>
                        <Typography className="file-link-text" variant="body2" sx={{
                          color: '#1976d2', fontSize: '13px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          transition: 'color 0.2s'
                        }}>
                          {fileName}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: '8px', minWidth: 400, p: 1 } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>Delete File</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#475569' }}>
            Are you sure you want to delete <strong>{fileToDelete?.original_filename || fileToDelete?.filename}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 500 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 500 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default UploadedDocuments;
